import { User } from "../../models/API";

export interface UserAttributes {
  self_intro?: string;
  timeout?: number;
  role?: string; // admin, operator, user
  default_team_id?: number; // Preferred team on sign-in, independent of membership source
  menu_order?: string[]; // Array of menu item labels in custom order
  cell_refs_enabled?: boolean; // Whether cell reference highlights are enabled (default true)
  margin_callouts_enabled?: boolean; // Whether margin callout cards are shown in WBR PDFs (default false)
  show_data_source_selector?: boolean; // Whether data source selector is shown in ChatBox (default false)
  dark_mode?: boolean; // Whether the UI uses dark mode (default: system preference)
  spektr_mcp_enabled?: boolean; // Whether the Spektr MCP (Ads Agent) tools are enabled in Ask AI (default false)
  yoy_forecast_mcp_enabled?: boolean; // Whether the YoY Forecast MCP tools are enabled in Ask AI (default false)
  clf_voc_mcp_enabled?: boolean; // Whether the CLF VOC (Voice of Customer) MCP tools are enabled in Ask AI (default false)
  // Add other attribute fields as needed
}

// Role constants
export const ROLE_ADMIN = "admin";
export const ROLE_OPERATOR = "operator";
export const ROLE_USER = "user";

// Helper function to get effective role (team_user_map overrides user table)
const roleRank = (role: string | null | undefined): number => {
  if (role === ROLE_ADMIN) return 3;
  if (role === ROLE_OPERATOR) return 2;
  if (role === ROLE_USER) return 1;
  return 0;
};

export const getEffectiveRole = (
  userAttributes: UserAttributes,
  teamUserMapAttributes: any
): string => {
  // Effective role = MAX(user-level global role, team-level role)
  const userRole = userAttributes.role || ROLE_USER;
  const teamRole = teamUserMapAttributes?.role || ROLE_USER;
  const highest = Math.max(roleRank(userRole), roleRank(teamRole));
  return highest >= 3 ? ROLE_ADMIN : highest >= 2 ? ROLE_OPERATOR : ROLE_USER;
};

// Helper function to check if user can access admin console
export const canAccessAdminConsole = (effectiveRole: string): boolean => {
  return effectiveRole === ROLE_ADMIN;
};

// Helper function to check if user can create/modify/delete public resources
export const canManagePublicResources = (effectiveRole: string): boolean => {
  return effectiveRole === ROLE_ADMIN || effectiveRole === ROLE_OPERATOR;
};

export const parseUserAttributes = (user: User | null): UserAttributes => {
  if (!user || !user.attributes) {
    return {};
  }

  try {
    // Parse the JSON string to an object
    const attributes =
      typeof user.attributes === "string"
        ? JSON.parse(user.attributes)
        : user.attributes;

    return attributes;
  } catch (error) {
    console.error("Error parsing user attributes:", error);
    return {};
  }
};

export const DEFAULT_TIMEOUT = 900;
export const DEFAULT_SELF_INTRO = "";
